import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import {
  BUDGET_CATEGORIES,
  MonthKey,
  YEAR_FLAG_VALUES,
  YEAR_FLAGS,
  YearFlag,
} from '@repo/constants'
import { monthStartString } from '@repo/zod-schemas'
import { QueryClient, useQuery } from '@tanstack/react-query'
import { z } from 'zod'

export const budgetTrackerSearchSchema = z.object({
  crtPeriod: monthStartString.optional(),
  budgetCategory: z.enum(BUDGET_CATEGORIES).optional(),
  year: z.enum(YEAR_FLAG_VALUES).optional(),
  isExpanded: z.boolean().optional(),
  keyword: z.string().optional(),
  loadedVersionId: z.number().optional(),
  manualInvestmentIds: z.array(z.number()).optional(),
  ...datatableSearchSchema.shape,
})

export type BudgetTrackerSearch = z.infer<typeof budgetTrackerSearchSchema>

export type BudgetTrackerResponse = Awaited<ReturnType<typeof fetchBudgetTracker>>
export type BudgetTrackerRow = BudgetTrackerResponse['results'][number]

type BudgetTrackerFilters = {
  crtPeriod?: string
  budgetCategory?: string
  year?: string
  keyword?: string
  manualInvestmentIds?: number[]
}

async function fetchBudgetTracker(filters: BudgetTrackerFilters, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['customer-investments']['budget-tracker'].$get(
    { query: stringifyValues(filters) },
    { init: { signal } },
  )
  return await res.json()
}

/** The tracker's cache key. Named so a patch can read the period and year it holds back off the key. */
type BudgetTrackerQueryKey = [
  'budgetTracker',
  crtPeriod: string | null,
  budgetCategory: string | null,
  year: string | null,
  keyword: string | null,
  manualInvestmentIds: string | null,
]

const budgetTrackerQueryKey = (filters: BudgetTrackerFilters): BudgetTrackerQueryKey => [
  'budgetTracker',
  filters.crtPeriod ?? null,
  filters.budgetCategory ?? null,
  filters.year ?? null,
  filters.keyword ?? null,
  filters.manualInvestmentIds?.length ? filters.manualInvestmentIds.join(',') : null,
]

export function useBudgetTracker(filters: BudgetTrackerFilters) {
  return useQuery({
    queryKey: budgetTrackerQueryKey(filters),
    queryFn: ({ signal }) => fetchBudgetTracker(filters, signal),
  })
}

// The view's own filters, so the file holds the rows the page shows (the general view keeps the K/E
// buckets out, the accounts view keeps only them).
type BudgetTrackerExportFilters = {
  crtPeriod?: string
  budgetCategory?: string
  year?: string
  keyword?: string
  manualInvestmentIds?: number[]
}

async function getBudgetTrackerCsv(filters: BudgetTrackerExportFilters, signal?: AbortSignal) {
  const params = new URLSearchParams()
  if (filters.crtPeriod) params.set('crtPeriod', filters.crtPeriod)
  if (filters.budgetCategory) params.set('budgetCategory', filters.budgetCategory)
  if (filters.year) params.set('year', filters.year)
  if (filters.keyword) params.set('keyword', filters.keyword)
  if (filters.manualInvestmentIds?.length) {
    params.set('manualInvestmentIds', JSON.stringify(filters.manualInvestmentIds))
  }
  const query = params.size > 0 ? `?${params.toString()}` : ''
  return fetchCsvExport(`/crt/customer-investments/budget-tracker/export${query}`, signal)
}

export function budgetTrackerExportQueryOptions(filters: BudgetTrackerExportFilters) {
  return {
    queryKey: [
      'budgetTrackerExport',
      filters.crtPeriod ?? null,
      filters.budgetCategory ?? null,
      filters.year ?? null,
      filters.keyword ?? null,
      filters.manualInvestmentIds?.join(',') ?? null,
    ],
    queryFn: ({ signal }: { signal: AbortSignal }) => getBudgetTrackerCsv(filters, signal),
  }
}

/**
 * Invalidates the budget-allocation tracker and its CSV. Any mutation that changes a figure the
 * tracker aggregates — estimates, manual investments — must call this.
 *
 * The export is served from the cache through `ensureQueryData`, which hands back whatever is
 * cached regardless of staleness, so it has to be removed outright rather than invalidated.
 */
export function invalidateBudgetTrackerQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['budgetTracker'] })
  queryClient.removeQueries({ queryKey: ['budgetTrackerExport'] })
}

/**
 * A left-to-spend balance per month it is spread over. With no month to spread over the whole balance
 * shows in one figure, rather than being divided by nothing or silently dropped — which happens for a
 * December current-year period, or a mask selecting only months outside the view's window.
 */
export function perMonthLeftToSpend(leftToSpend: number, activeMonthCount: number): number {
  return activeMonthCount > 0 ? leftToSpend / activeMonthCount : leftToSpend
}

/**
 * What saving a bucket's month mask hands back: the stored selection, and the months it resolves to in each
 * year view. Structural on purpose — the endpoint's response type is checked against it at the call site.
 */
type SavedLeftToSpendMask = {
  budgetAllocation: string
  months: MonthKey[]
  leftToSpendActiveMonths: Record<YearFlag, MonthKey[]>
}

/**
 * Rewrites one bucket's month mask in every cached tracker response for the period, so the monthly
 * left-to-spend column updates the moment a mask is saved. A refetch would be wasted work: the mask
 * changes only how a balance is divided, never any amount behind it.
 *
 * One mask serves both year views and both can be cached at once, so the active months are taken from the
 * window the server resolved for *that* entry's year — read off the query key, since the mask itself says
 * nothing about which view is looking at it. Entries for another period are left alone.
 */
export function patchLeftToSpendMask(
  queryClient: QueryClient,
  result: SavedLeftToSpendMask,
  crtPeriod: string,
) {
  // The export's monthly column spreads the same balances, so its cached CSV is now wrong.
  queryClient.removeQueries({ queryKey: ['budgetTrackerExport'] })

  // The mask changes how the tracker spread each balance across months, but no amount
  // behind it — so the cached rows are patched from the response rather than refetched.
  for (const [queryKey] of queryClient.getQueriesData<BudgetTrackerResponse>({
    queryKey: ['budgetTracker'],
  })) {
    const [, keyPeriod, , keyYear] = queryKey as BudgetTrackerQueryKey
    if (keyPeriod !== crtPeriod) continue
    const activeMonths =
      result.leftToSpendActiveMonths[keyYear === YEAR_FLAGS.NY ? YEAR_FLAGS.NY : YEAR_FLAGS.CY]

    queryClient.setQueryData<BudgetTrackerResponse>(queryKey, (previous) => {
      if (!previous) return previous
      return {
        ...previous,
        results: previous.results.map((row) =>
          row.budgetAllocation === result.budgetAllocation
            ? {
                ...row,
                leftToSpendMonths: result.months,
                leftToSpendActiveMonths: activeMonths,
                monthlyLeftToSpend: perMonthLeftToSpend(row.leftToSpend, activeMonths.length),
              }
            : row,
        ),
      }
    })
  }
}
